/**
 * School Icon Library API Contracts (Custom SVG Menu Icons plan, U3/U4).
 *
 * Create, list, read, rename, replace, and delete a school's own library of
 * sanitized SVG menu icons. See `schemas/entities/school-icon-library.ts` for
 * the stored entity shape and the namespaced-reference grammar a menu item's
 * existing `icon` field uses to point at one of these.
 *
 * KTD18: artwork travels as a plain UTF-8 string in the JSON body — no
 * multipart, no presigned upload, no object store. `MAX_MARKUP_UPLOAD_BYTES`
 * mirrors the backend sanitize pipeline's own `MAX_INPUT_BYTES`
 * (`apps/backend/src/services/school-icon-library/sanitize/constants.ts`);
 * kept as a literal here (shared-types cannot import backend source) rather
 * than re-exported, so a change to one number does not silently drift the
 * other — the pipeline is the actual enforcement point regardless of what
 * this bound catches first.
 *
 * KTD8: every request that names an existing entry does so by its
 * `SchoolIconLibraryEntry.publicId` — never by name, and the school itself is
 * never a request field; the backend pins it from the session.
 */

import { z } from 'zod'
import { SchoolIconLibraryEntrySchema } from '../../schemas/entities/school-icon-library'

/** Mirrors the sanitize pipeline's `MAX_INPUT_BYTES` (KTD14, KTD18). */
export const MAX_MARKUP_UPLOAD_BYTES = 32 * 1024

const MarkupFieldSchema = z
  .string()
  .min(1, 'markup is required')
  .refine(value => new TextEncoder().encode(value).length <= MAX_MARKUP_UPLOAD_BYTES, {
    message: `markup must be at most ${MAX_MARKUP_UPLOAD_BYTES} bytes (UTF-8)`
  })

const NameFieldSchema = z.string().trim().min(1, 'name is required').max(120)

// ============================================================================
// POST /v1/school-icon-library/entries
// ============================================================================

export const CreateIconLibraryEntryRequestSchema = z.object({
  name: NameFieldSchema,
  markup: MarkupFieldSchema
})

export type CreateIconLibraryEntryRequest = z.infer<typeof CreateIconLibraryEntryRequestSchema>

export const IconLibraryEntryResponseSchema = z.object({
  message: z.string(),
  data: SchoolIconLibraryEntrySchema
})

export type IconLibraryEntryResponse = z.infer<typeof IconLibraryEntryResponseSchema>

// ============================================================================
// GET /v1/school-icon-library/entries
// ============================================================================

export const ListIconLibraryEntriesResponseSchema = z.object({
  message: z.string(),
  data: z.array(SchoolIconLibraryEntrySchema)
})

export type ListIconLibraryEntriesResponse = z.infer<typeof ListIconLibraryEntriesResponseSchema>

// ============================================================================
// GET /v1/school-icon-library/entries/:entryId
// ============================================================================

export const GetIconLibraryEntryResponseSchema = IconLibraryEntryResponseSchema
export type GetIconLibraryEntryResponse = IconLibraryEntryResponse

// ============================================================================
// PATCH /v1/school-icon-library/entries/:entryId — rename
// ============================================================================

export const RenameIconLibraryEntryRequestSchema = z.object({
  name: NameFieldSchema
})

export type RenameIconLibraryEntryRequest = z.infer<typeof RenameIconLibraryEntryRequestSchema>

export const RenameIconLibraryEntryResponseSchema = IconLibraryEntryResponseSchema
export type RenameIconLibraryEntryResponse = IconLibraryEntryResponse

// ============================================================================
// PUT /v1/school-icon-library/entries/:entryId/markup — replace artwork
// ============================================================================

export const ReplaceIconLibraryEntryMarkupRequestSchema = z.object({
  markup: MarkupFieldSchema
})

export type ReplaceIconLibraryEntryMarkupRequest = z.infer<typeof ReplaceIconLibraryEntryMarkupRequestSchema>

export const ReplaceIconLibraryEntryMarkupResponseSchema = IconLibraryEntryResponseSchema
export type ReplaceIconLibraryEntryMarkupResponse = IconLibraryEntryResponse

// ============================================================================
// DELETE /v1/school-icon-library/entries/:entryId
// ============================================================================

/**
 * One place a deleted-but-referenced icon is still used, surfaced so an
 * admin can go fix it. `surface` names WHERE the reference lives; `label` is
 * the best available human-readable identity for that location (a menu
 * item's own label, or the page's title); `detail` adds disambiguating
 * context (e.g. a page's publish status) when `label` alone is not enough.
 */
export const IconLibraryReferenceLocationSchema = z.object({
  surface: z.enum(['menu', 'page']).describe('Where the reference lives — the nav/menu config, or a page version'),
  label: z.string().describe("The referencing item's own label/title, or the enclosing page's title"),
  detail: z.string().nullable().describe('Extra disambiguating context, e.g. a page publish status')
})

export type IconLibraryReferenceLocation = z.infer<typeof IconLibraryReferenceLocationSchema>

/**
 * 409 body when a delete is refused because the entry is still referenced
 * (R4, AE7, AE8). Shape matches the backend's standard error envelope
 * (`{ error: { type, message, details } }`) — `details.references` is the
 * list an admin acts on.
 */
export const IconLibraryEntryStillReferencedResponseSchema = z.object({
  error: z.object({
    type: z.literal('ICON_LIBRARY_STILL_REFERENCED'),
    message: z.string(),
    details: z.object({
      references: z.array(IconLibraryReferenceLocationSchema)
    })
  })
})

export type IconLibraryEntryStillReferencedResponse = z.infer<typeof IconLibraryEntryStillReferencedResponseSchema>

export const DeleteIconLibraryEntryResponseSchema = z.object({
  message: z.string()
})

export type DeleteIconLibraryEntryResponse = z.infer<typeof DeleteIconLibraryEntryResponseSchema>
